// react
import { useMemo } from 'react';
import { NavigateFunction } from 'react-router-dom';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import LoadingServiceContent from './LoadingServiceContent';
import { Button } from '@/components/atoms/Button/Button';
import { CallToAction } from '@/components/atoms/CallToAction/CallToAction';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';

import { SanityContent } from '@/components/organisms/SanityContent/SanityContent';

// store
import { Service as ServiceType } from '@karehero/models';

// utils
import { buildImageUrl } from '@/utils/sanity';

// hooks
import { useWindowSize } from '@/hooks';

// theme
import { theme } from '@/theme';

/**
 * ServiceContent displays the content for a single article.
 */
export const ServiceContent = ({
  content,
  isLoading = true,
  navigate,
  backPath,
  onAction,
  onBack,
}: ServiceContentProps) => {
  // hooks
  const [width] = useWindowSize();

  // memo
  const isCtaMobile = useMemo(
    () => width < theme.breakpointValues.lgValue,
    [width],
  );

  const headerImageUrl = useMemo(
    () => buildImageUrl(content?.headerImageUrl, 800),
    [content],
  );

  // methods
  const onActionPress = () => {
    if (content?.callToActionCard?.buttonLink) {
      navigate?.(content.callToActionCard.buttonLink);
    } else {
      onAction?.();
    }
  };

  return (
    <StyledServiceContentPage>
      {onBack && (
        <div>
          <Button
            ariaLabel='back'
            iconLeft='chevron-left'
            variant='secondary'
            size='sm'
            onPress={onBack}
          >
            Back
          </Button>
        </div>
      )}
      <StyledServiceContent>
        <LoadingTransition
          isLoading={isLoading}
          loading={
            <Skeleton shape='header-image' isRounded={false} isMargin={false} />
          }
          content={
            <HeaderImage>
              <img
                src={headerImageUrl}
                alt={content?.title}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src =
                    'https://cdn.sanity.io/images/x7r1t5o8/production/e8579e2a4c3766cf0cf99a24a9d0679d2d2bbeb4-5322x2365.jpg?h=800';
                }}
              />
            </HeaderImage>
          }
        />
        <Wrapper>
          <LoadingTransition
            isLoading={isLoading}
            loading={<LoadingServiceContent />}
            content={
              <ServiceContentWrapper>
                <Title>{content?.title}</Title>
                <SanityContent content={content?.content} />
              </ServiceContentWrapper>
            }
          />
          <CallToAction
            isLoading={isLoading}
            title={content?.callToActionCard?.title || 'Questions about care?'}
            text={
              content?.callToActionCard?.subTitle ||
              'Our friendly care experts will provide guidance and help you explore your funding and benefit options, book a call now!'
            }
            action={content?.callToActionCard?.buttonText || 'Book a call'}
            onPress={onActionPress}
            isMobile={isCtaMobile}
          />
        </Wrapper>
      </StyledServiceContent>
    </StyledServiceContentPage>
  );
};

const StyledServiceContentPage = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[12]};
`;

const StyledServiceContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[48]};
`;

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${(props) => props.theme.spacing[128]};
`;

const ServiceContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${(props) => props.theme.spacing[192]};
  ${(props) =>
    props.theme.breakpoint.lg(css`
      margin-bottom: ${props.theme.spacing[32]};
    `)};
`;

const HeaderImage = styled.div`
  border-radius: 8px;
  overflow: hidden;
  height: 180px;
  ${(props) =>
    props.theme.breakpoint.sm(css`
      height: 300px;
    `)}
  display: flex;
  align-items: center;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    user-drag: none;
    -webkit-user-drag: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.heading['7xl']};
  color: ${(props) => props.theme.color.primary[80]};
  margin-bottom: ${(props) => props.theme.spacing[32]};
`;

interface ServiceContentProps {
  content?: ServiceType;
  isLoading?: boolean;
  navigate?: NavigateFunction;
  backPath?: string;
  onAction?: () => void;
  onBack?: () => void;
}

ServiceContent.displayName = 'ServiceContent';
export default ServiceContent;
